import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../../../lib/api";
import { SectionCard, EmptyState } from "../shared";
import { Users, FileSignature, ChevronRight } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────
// Section 1 — Agreements
//   Aggregate: one-line table of every buyer's agreement status.
//   Per-buyer: inline agreement editor (no drawer).
// ─────────────────────────────────────────────────────────────────────────
export default function AgreementsAggregateSection({ buyers }) {
  const [statusByBuyer, setStatusByBuyer] = useState({});
  const [loading, setLoading] = useState(true);
  const [, setUrlSearch] = useSearchParams();

  useEffect(() => {
    if (buyers.length === 0) { setLoading(false); return; }
    let dead = false;
    (async () => {
      const map = {};
      // Fetch terms for each buyer. Small parallelism keeps it fast.
      await Promise.all(buyers.map(async (b) => {
        try {
          const r = await api.get(`/dropship/terms/${b.id}`);
          const t = r.data || {};
          const signedS = t.seller_signed_at || t.signed_at_seller || null;
          const signedB = t.buyer_signed_at  || t.signed_at_buyer  || null;
          const locked  = t.locked_at || (signedS && signedB);
          map[b.id] = {
            status: locked ? "signed" : (signedS ? "awaiting_buyer" : signedB ? "awaiting_seller" : "not_started"),
            signed_at: locked || null,
            has_amendment: !!t.pending_amendment,
          };
        } catch {
          map[b.id] = { status: "not_started", signed_at: null, has_amendment: false };
        }
      }));
      if (!dead) { setStatusByBuyer(map); setLoading(false); }
    })();
    return () => { dead = true; };
  }, [buyers]);

  const pickPartner = (bid) => {
    const next = new URLSearchParams(window.location.search);
    next.set("partner", bid);
    setUrlSearch(next, { replace: true });
  };

  return (
    <SectionCard
      icon={FileSignature}
      title="Step 1 · Agreements"
      subtitle="Bilateral contract per buyer — MAP, returns, delays, damage policy. Both parties sign to lock terms."
      testId="setup-section-agreements"
      defaultOpen={false}
    >
      {loading ? (
        <div className="p-6 text-sm text-foreground/50">Loading agreement status…</div>
      ) : buyers.length === 0 ? (
        <EmptyState icon={Users} title="No connected buyers yet" body="Once a buyer accepts your invitation, their agreement appears here." />
      ) : (
        <div className="divide-y divide-border">
          {buyers.map(b => {
            const s = statusByBuyer[b.id] || { status: "not_started" };
            const meta = AGREEMENT_STATUS_META[s.status] || AGREEMENT_STATUS_META.not_started;
            return (
              <div key={b.id} className="flex items-center gap-4 px-5 py-3" data-testid={`setup-agreement-row-${b.id}`}>
                <div className="w-8 h-8 rounded-lg bg-indigo-50 grid place-items-center shrink-0">
                  <Users className="w-3.5 h-3.5 text-indigo-700"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium truncate">{b.company || b.name}</div>
                  <div className="text-[11px] text-foreground/55 truncate">
                    {s.signed_at ? `Signed ${new Date(s.signed_at).toLocaleDateString()}` : "Not yet signed"}
                    {s.has_amendment && " · Amendment pending"}
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1.5 text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${meta.cls}`}>
                  {meta.label}
                </span>
                <button
                  onClick={() => pickPartner(b.id)}
                  data-testid={`setup-agreement-open-${b.id}`}
                  className="text-[11.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 border border-border hover:bg-secondary/50 inline-flex items-center gap-1.5"
                >
                  View <ChevronRight className="w-3 h-3"/>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

const AGREEMENT_STATUS_META = {
  signed:          { label: "Signed",          cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  awaiting_buyer:  { label: "Awaiting buyer",  cls: "bg-amber-50 text-amber-700 border-amber-200" },
  awaiting_seller: { label: "Awaiting you",    cls: "bg-amber-50 text-amber-700 border-amber-200" },
  not_started:    { label: "Not started",     cls: "bg-secondary text-foreground/60 border-border" },
};
